import React from 'react';
import { addNotification } from 'utils/NotificationManager';
import { KeysOfType } from 'component_utils/typescriptUtils';
import Translation from 'utils/Language/Translation';
import { IBatch, IBinLocation, IItem, IItemBinLocation, ISerialNumber } from 'interfaces';
import { MaybePromise, distinctBy, mapNotNil } from 'component_utils/utils';
import { CompoundBarcode, compoundItemBarcodeParser } from 'utils/Config';
import { OnActionArgs, OnEnterArgs, State } from 'components/StateMachine';
import { IApi } from 'utils/API';
import { isBatch, isNormal, isSerial } from 'component_utils/itemLocationTree';
import { cloneDeep, isNil, range } from 'lodash';
import { batchAutocompleteOptions, itemAutocompleteOptions, matchBatch, matchItem, matchSerial, serialAutocompleteOptions, simpleAutocompleteOptions } from 'component_utils/objectUtils';
import StateDecide from '../decision';
import { IConfigDto } from 'interfaces';
import { AutoCompleteFetcher } from 'components/Autocomplete';
import Item from 'components/objects/item';

type FindItems<S, P, R> = (state: OnEnterArgs<S, P, R>, codes: string[]) => IItem[];
type FindPossibleSerialsForItemScan<S, P, R> = (state: OnEnterArgs<S, P, R>) => ISerialNumber[];
type FindAllowedItems<S, P, R> = (state: OnEnterArgs<S, P, R>) => IItem[];

type FindAllowedBatches<S, P, R> = (state: OnEnterArgs<S, P, R>) => IBatch[];

type FindAllowedLots<S, P, R> = (state: OnEnterArgs<S, P, R>) => string[];
type FindAllowedSerials<S, P, R> = (state: OnEnterArgs<S, P, R>) => ISerialNumber[];

interface EventProps {
  api: IApi;
  config: IConfigDto;
}

interface Attributes {
  hint: string;
  autocompleter?: AutoCompleteFetcher;
}

interface Config<S, P, A, R, T> {
  binLocationKey?: KeysOfType<S, IBinLocation>;
  binLocationContentsKey?: KeysOfType<S, IItemBinLocation[]>;
  compoundBarcodeKey: KeysOfType<S, CompoundBarcode>;

  itemKey: KeysOfType<S, IItem>;

  batchKey: KeysOfType<S, IBatch>;

  lotKey: KeysOfType<S, string>;
  serialKey: KeysOfType<S, ISerialNumber>;

  quantityKey: KeysOfType<S, number>;

  liquidContainerSupport: boolean;

  itemStateName: number;
  onItemSetCustomStateName?: number;
  onItemSetContinueStateName?: number;

  batchStateName: number;

  lotStateName: number;

  shouldScanSerialStateName: number;
  
  serialStateName: number;

  nextState: number;

  itemState: {
    findAllItems: FindItems<S, P, R>;
    findAllPossibleSerialsForItemScan: FindPossibleSerialsForItemScan<S, P, R>;

    findAllowedItems?: FindAllowedItems<S, P, R>;

    checkItemIsAllowed?: (state: OnEnterArgs<S, P, R>, item: IItem) => MaybePromise<boolean>;

    afterItemSelected?: (state: OnEnterArgs<S, P, R>, item: IItem) => MaybePromise<any>;

    attributes?: Partial<A>;
  }

  batchState: {
    findAllowedBatches: FindAllowedBatches<S, P, R>;

    attributes?: Partial<A>;
  }

  serialStates: {
    findAllowedLots: FindAllowedLots<S, P, R>;
    findAllowedSerials: FindAllowedSerials<S, P, R>;

    lotAttributes?: Partial<A>;
    serialAttributes?: Partial<A>;
  }
}

interface ResolveStockLinesArgs {
  fromLocation: IBinLocation;
  availableSerials: { serial: ISerialNumber, qty: number }[];
  availableBatches: { batch: IBatch, qty: number }[];
  
  item: IItem;
  batch: IBatch;

  lot: string;
  serial: ISerialNumber;

  quantity: number;
}

export interface ResolvedStockLine {
  fromLocation: IBinLocation;
  item: IItem;
  batch: IBatch;
  lot: string;
  serial: ISerialNumber;
  quantity: number;
}

export const resolveStockLines = function({ 
  fromLocation, item, batch, lot, serial, quantity, availableSerials, availableBatches
}: ResolveStockLinesArgs): ResolvedStockLine[] {
  let contentLines: { batch: IBatch; serial: ISerialNumber; quantity: number }[] = []
  if (isNormal(item)) {
    contentLines = [{ serial: null, batch: null, quantity: quantity }]
  } else if (isBatch(item)) {
    contentLines = availableBatches.map(it => ({ serial: null, batch: it.batch, quantity: it.qty }))
  } else if (isSerial(item)) {
    contentLines = availableSerials.map(it => ({ serial: it.serial, batch: null, quantity: it.qty }))
  }

  // liquid containers only 
  if (fromLocation.physicalLocationType === 'LIQUID_CONTAINER') {
    const itemContents = cloneDeep(contentLines.filter(it => it.quantity > 0))
    const lines: ResolvedStockLine[] = []
    let neededQty = quantity
    let index = 0
    while (neededQty > 0 && index < itemContents.length) {
      const contentLine = itemContents[index]
      const takeQty = Math.min(contentLine.quantity, neededQty)
      if (takeQty > 0) {
        lines.push({
          fromLocation,

          item: item,

          batch: contentLine.batch,

          lot: contentLine.serial?.lotCode || null,
          serial: contentLine.serial,

          quantity: takeQty,
        })
      }
      contentLine.quantity -= takeQty
      neededQty -= takeQty
      index += 1
    }
    if (neededQty > 0) {
      throw addNotification('danger', <Translation name="T.errors.notEnoughAvailableStock"/>)
    }
    return lines
  }

  // fill the serials list if managed by dispatch
  if (isSerial(item) && item.serialManagedByDispatch) {
    const availableSerials = contentLines
      .filter(it => it.quantity > 0 && 
                    it.serial.lotCode === lot)
      .map(it => it.serial)
    if (availableSerials.length < quantity) {
      throw addNotification('danger', <Translation name="T.errors.notEnoughSerialsAvailable"/>)
    }
    return range(quantity).map(() => ({
      fromLocation,

      item,
      
      batch,

      lot,
      serial: availableSerials.pop(),
      
      quantity: 1.0
    }))
  }

  // all data should be available
  return [{
    fromLocation,

    item,
    
    batch,

    lot,
    serial,
    
    quantity
  }]
}

const createItemIdentifications = function <S, P extends EventProps, A extends Attributes, R, T>({
  binLocationKey,
  binLocationContentsKey,
  compoundBarcodeKey,

  itemKey,

  batchKey,

  lotKey,
  serialKey,

  quantityKey, 

  liquidContainerSupport,

  itemStateName,
  onItemSetCustomStateName,
  onItemSetContinueStateName,

  batchStateName,

  lotStateName,

  shouldScanSerialStateName,
  
  serialStateName,

  nextState,

  itemState: {
    findAllItems,
    findAllPossibleSerialsForItemScan,

    findAllowedItems,

    checkItemIsAllowed,

    afterItemSelected,

    ...itemStateProps
  },

  batchState: {
    findAllowedBatches,

    ...batchStateProps
  },

  serialStates: {
    findAllowedLots,
    findAllowedSerials,

    ...serialStateProps
  }
}: Config<S, P, A, R, T>): { [k: number]: State<S, P, A, R> } {
  if (!isNil(onItemSetCustomStateName) && isNil(onItemSetContinueStateName)) {
    throw new Error("onItemSetContinueStateName should be set if onItemSetCustomStateName is set")
  }

  const itemState: State<S, EventProps, Attributes, R> = {
    onEnter: async (args: OnEnterArgs<S, P, R>) => {
      const { state } = args
      const isLiquidContainer = (binLocationKey && (state[binLocationKey] as unknown as IBinLocation).physicalLocationType === 'LIQUID_CONTAINER')
      const isSingleItemLocation = (binLocationKey && (state[binLocationKey] as unknown as IBinLocation).physicalLocationType === 'SINGLE_ITEM_LOCATION')
      if ((isLiquidContainer && liquidContainerSupport) || isSingleItemLocation) {
        const location = state[binLocationKey] as unknown as IBinLocation
        let items: IItem[] = []
        if (binLocationContentsKey) {
          const contents = state[binLocationContentsKey] as unknown as IItemBinLocation[]
          items = distinctBy(contents.map(it => it.item), it => it.code)
        } else if (findAllowedItems) {
          items = distinctBy(await findAllowedItems(args), it => it.code)
        } else {
          const contents = await args.props.api.binLocation.getBinLocationContents(location.code)
          items = distinctBy(contents.map(it => it.item), it => it.code)
        }

        if (items.length > 1) {
          // allow users to still specify what item to remove from the container
          addNotification('danger', <Translation name="T.errors.tooManyItemsInLiquidContainer" params={{numOfItems: items.length}} />);
          return;
        }
        if (items.length === 0) {
          throw addNotification('danger', <Translation name="T.errors.noItemsInLiquidContainer" />);
        }

        const item = items[0]
        if (checkItemIsAllowed && !checkItemIsAllowed(args, item)) {
          throw addNotification('danger', <Translation name="T.errors.itemIdentification.itemIsNotAllowed" params={{ item: <Item inline obj={item}/> }} />);
        }

        if (findAllowedItems && !(await findAllowedItems(args)).find(it => matchItem(it, item.code))) {
          throw addNotification('danger', <Translation name="T.errors.itemIdentification.itemIsNotAllowed" params={{ item: <Item inline obj={item}/> }} />);
        }
        
        // move to the next state
        await afterItemSelected?.(args, item)
        if (isNormal(item) || (isLiquidContainer && liquidContainerSupport)) {
          await window.be_specializations?.augmentations?.identification?.itemSelected?.({ item })
          return {
            type: 'MOVE',
            data: {
              [itemKey]: item,
              [compoundBarcodeKey]: { compoundType: 'raw', itemCode: item.code, item },
              __be_item_identification_next_state: nextState
            } as any,
            nextState: isNil(onItemSetCustomStateName) ? nextState : onItemSetCustomStateName,
          };
        } else if (isBatch(item)) {
          await window.be_specializations?.augmentations?.identification?.itemSelected?.({ item })
          return {
            type: 'MOVE',
            data: {
              [itemKey]: item,
              [compoundBarcodeKey]: { compoundType: 'raw', itemCode: item.code, item },
              __be_item_identification_next_state: batchStateName
            } as any,
            nextState: isNil(onItemSetCustomStateName) ? batchStateName : onItemSetCustomStateName,
          };  
        } else if (isSerial(item)) {
          await window.be_specializations?.augmentations?.identification?.itemSelected?.({ item })
          return {
            type: 'MOVE',
            data: {
              [itemKey]: item,
              [compoundBarcodeKey]: { compoundType: 'raw', itemCode: item.code, item },
              __be_item_identification_next_state: lotStateName
            } as any,
            nextState: isNil(onItemSetCustomStateName) ? lotStateName : onItemSetCustomStateName,
          };  
        } else {
          throw new Error("Impossible type")
        }
      }
    },
    onAction: async (args: OnActionArgs<S, P, R>) => {
      const { state, inputs: [input] } = args

      // first check whether this is a compound
      let compound = await compoundItemBarcodeParser(
        input, 
        (codes) => findAllItems(args, codes),
        'itemCode',
        s => findAllPossibleSerialsForItemScan(args).filter(it => matchSerial(it, s))
      )
      let item = compound.item

      if (!item) {
        throw addNotification('danger', <Translation name="T.errors.itemIdentification.unknownItemForCode" params={{ code: input }} />);
      }

      if (checkItemIsAllowed && !checkItemIsAllowed(args, item)) {
        throw addNotification('danger', <Translation name="T.errors.itemIdentification.itemIsNotAllowed" params={{ item: <Item inline obj={item}/> }} />);
      }

      if (findAllowedItems && !(await findAllowedItems(args)).find(it => matchItem(it, item.code))) {
        throw addNotification('danger', <Translation name="T.errors.itemIdentification.itemIsNotAllowed" params={{ item: <Item inline obj={item}/> }} />);
      }

      const isLiquidContainer = (binLocationKey && (state[binLocationKey] as unknown as IBinLocation).physicalLocationType === 'LIQUID_CONTAINER')

      await afterItemSelected?.(args, item)
      if (isNormal(item) || (isLiquidContainer && liquidContainerSupport)) {
        await window.be_specializations?.augmentations?.identification?.itemSelected?.({ item })
        return {
          type: 'MOVE',
          data: {
            [itemKey]: item,
            [compoundBarcodeKey]: compound,
            __be_item_identification_next_state: nextState
          } as any,
          nextState: isNil(onItemSetCustomStateName) ? nextState : onItemSetCustomStateName,
        };
      } else if (isBatch(item)) {
        await window.be_specializations?.augmentations?.identification?.itemSelected?.({ item })
        return {
          type: 'MOVE',
          data: {
            [itemKey]: item,
            [compoundBarcodeKey]: compound,
            __be_item_identification_next_state: batchStateName
          } as any,
          nextState: isNil(onItemSetCustomStateName) ? batchStateName : onItemSetCustomStateName,
        };  
      } else if (isSerial(item)) {
        await window.be_specializations?.augmentations?.identification?.itemSelected?.({ item })
        return {
          type: 'MOVE',
          data: {
            [itemKey]: item,
            [compoundBarcodeKey]: compound,
            __be_item_identification_next_state: lotStateName
          } as any,
          nextState: isNil(onItemSetCustomStateName) ? lotStateName : onItemSetCustomStateName,
        };  
      } else {
        throw new Error("Impossible type")
      }
    },

    attributes: (args: OnEnterArgs<S, P, R>) => {
      const { state, props: { api }} = args
      let autocompleter = api.item.findAutocomplete
      if (binLocationContentsKey) {
        const contents = state[binLocationContentsKey] as unknown as IItemBinLocation[];
        const items = distinctBy(mapNotNil(contents, it => it.item), it => it.code)
        autocompleter = async s => itemAutocompleteOptions(items, s) as any[]
      } else if (findAllowedItems) {
        autocompleter = async s => itemAutocompleteOptions(await findAllowedItems(args), s) as any[]
      }

      return {
        hint: 'T.hints.item',
        autocompleter,
        ...(itemStateProps.attributes || {})
      }
    },

    nodeName: 'T.states.item',
    outputs: isNil(onItemSetCustomStateName) ? [
      {
        name: 'onLiquid',
        to: nextState,
      },
      {
        name: 'onNormal',
        to: nextState,
      },
      {
        name: 'onBatch',
        to: batchStateName,
      },
      {
        name: 'onSerial',
        to: lotStateName,
      },
    ] : [
      {
        name: 'onItem',
        to: onItemSetCustomStateName,
      },
    ], 
  }

  const batchState: State<S, EventProps, Attributes, R> = {
    onEnter: async (args: OnEnterArgs<S, P, R>) => {
      // check the compound
      const item = args.state[itemKey] as unknown as IItem;
      const compound = args.state[compoundBarcodeKey] as CompoundBarcode
      if (!isNil(compound?.batchCode)) {
        const foundBatch = (await findAllowedBatches(args)).find(b => matchBatch(b, compound.batchCode))
        if (foundBatch) {
          await window.be_specializations?.augmentations?.identification?.batchSelected?.({ item, batch: foundBatch })
          return {
            type: 'MOVE',
            data: {
              [batchKey]: foundBatch,
            } as any,
            nextState,
          };
        } else {
          addNotification('danger', <Translation name="T.errors.itemIdentification.unknownBatchForCode" params={{ code: compound?.batchCode }} />);
          args.rollbackUntilAndInterruptFirst(itemStateName)
          return
        }
      }

      // attempt auto fill
      if (args.props.config.settings.scannerItemIdentificationBatchAllowAutoSelectIfOnlyOneOnLocation && binLocationContentsKey) {
        const contents = args.state[binLocationContentsKey] as unknown as IItemBinLocation[];
        const batches = distinctBy(
          contents.filter((it) => it.item.code === item.code).map(it => it.batch),
          (it) => it.visualCode
        );
        if (batches.length === 1) {
          const foundBatch = (await findAllowedBatches(args)).find(b => matchBatch(b, batches[0].code))
          if (!isNil(foundBatch)) {
            await window.be_specializations?.augmentations?.identification?.batchSelected?.({ item, batch: foundBatch })
            return {
              type: 'MOVE',
              data: {
                [batchKey]: foundBatch,
              } as any,
              nextState,
            };
          } else {
            addNotification('danger', <Translation name="T.errors.itemIdentification.unknownBatchForCode" params={{ code: compound?.batchCode }} />);
            args.rollbackUntilAndInterruptFirst(itemStateName)
            return
          }
        }
      }

      if (args.props.config.settings.scannerItemIdentificationBatchAllowAutoSelectIfOnlyOneAllowed) {      
        const batches = await findAllowedBatches(args)
        if (batches.length === 1) {
          await window.be_specializations?.augmentations?.identification?.batchSelected?.({ item, batch: batches[0] })
          return {
            type: 'MOVE',
            data: {
              [batchKey]: batches[0],
            } as any,
            nextState,
          };
        }
      }
    },
    onAction: async (args: OnActionArgs<S, P, R>) => {
      const item = args.state[itemKey] as unknown as IItem;
      const input = args.inputs[0];
      const { batchCode } = await compoundItemBarcodeParser(input, () => [args.state[itemKey] as unknown as IItem], 'batchCode')
      const batch = await findAllowedBatches(args).find(b => matchBatch(b, batchCode))
      if (isNil(batch)) {
        throw addNotification('danger', <Translation name="T.errors.itemIdentification.unknownBatchForCode" params={{ code: batchCode }} />);
      }
      await window.be_specializations?.augmentations?.identification?.batchSelected?.({ item, batch })
      return {
        type: 'MOVE',
        data: {
          [batchKey]: batch,
        } as any,
        nextState,
      };
    },

    attributes: (args: OnEnterArgs<S, P, R>) => {
      const { state } = args
      let autocompleter: AutoCompleteFetcher = null
      if (binLocationContentsKey) {
        const contents = state[binLocationContentsKey] as unknown as IItemBinLocation[];
        const item = state[itemKey] as unknown as IItem;
        const batches = distinctBy(mapNotNil(contents.filter(it => it.item.code === item.code), it => it.batch), it => it.code)
        autocompleter = async s => batchAutocompleteOptions(batches, s) as any[]
      } else {
        autocompleter = async s => batchAutocompleteOptions(await findAllowedBatches(args), s) as any[]
      }

      return {
        hint: 'T.hints.batch',
        autocompleter,
        ...(batchStateProps.attributes || {})
      }
    },


    nodeName: 'T.states.batch',
    outputs: [
      {
        name: 'batch',
        to: nextState,
      },
    ],
  }

  const lotState: State<S, EventProps, Attributes, R> = {
    onEnter: async (args: OnEnterArgs<S, P, R>) => {
      // check the compound
      const compound = args.state[compoundBarcodeKey] as CompoundBarcode
      const item = args.state[itemKey] as unknown as IItem;
      if (!isNil(compound?.batchCode)) {
        const foundBatch = (await findAllowedLots(args)).find(b => b.toLowerCase().trim() === compound.batchCode.toLowerCase().trim())
        if (!isNil(foundBatch)) {
          await window.be_specializations?.augmentations?.identification?.lotSelected?.({ item, lot: foundBatch })
          return {
            type: 'MOVE',
            data: {
              [lotKey]: foundBatch,
            } as any,
            nextState: shouldScanSerialStateName,
          };
        } else {
          addNotification('danger', <Translation name="T.errors.itemIdentification.unknownBatchForCode" params={{ code: compound?.batchCode }} />);
          args.rollbackUntilAndInterruptFirst(itemStateName)
          return
        }
      }

      // check if we use lots
      if (args.props.config.settings.scannerItemIdentificationLotAlwaysEmpty) {
        const foundBatch = (await findAllowedLots(args)).find(b => b.toLowerCase().trim() === "".toLowerCase().trim())
        if (!isNil(foundBatch)) {
          await window.be_specializations?.augmentations?.identification?.lotSelected?.({ item, lot: "" })
          return {
            type: 'MOVE',
            data: {
              [lotKey]: "",
            } as any,
            nextState: shouldScanSerialStateName,
          };
        } else {
          addNotification('danger', <Translation name="T.errors.itemIdentification.unknownBatchForCode" params={{ code: "" }} />);
          args.rollbackUntilAndInterruptFirst(itemStateName)
          return
        }
      }
      
      // attempt auto fill
      if (args.props.config.settings.scannerItemIdentificationLotAllowAutoSelectIfOnlyOneOnLocation && binLocationContentsKey) {
        const contents = args.state[binLocationContentsKey] as unknown as IItemBinLocation[];
        const item = args.state[itemKey] as unknown as IItem;
    
        const batches = distinctBy(
          contents.filter((it) => it.item.code === item.code).map(it => it.serialNumber.lotCode),
          (it) => it
        );
        if (batches.length === 1) {
          const foundBatch = (await findAllowedLots(args)).find(b => b.toLowerCase().trim() === batches[0].toLowerCase().trim())
          if (!isNil(foundBatch)) {
            await window.be_specializations?.augmentations?.identification?.lotSelected?.({ item, lot: foundBatch })
            return {
              type: 'MOVE',
              data: {
                [lotKey]: foundBatch,
              } as any,
              nextState: shouldScanSerialStateName,
            };
          } else {
            addNotification('danger', <Translation name="T.errors.itemIdentification.unknownBatchForCode" params={{ code: compound?.batchCode }} />);
            args.rollbackUntilAndInterruptFirst(itemStateName)
            return
          }
        }
      }

      if (args.props.config.settings.scannerItemIdentificationLotAllowAutoSelectIfOnlyOneAllowed) {      
        const batches = await findAllowedLots(args)
        if (batches.length === 1) {
          await window.be_specializations?.augmentations?.identification?.lotSelected?.({ item, lot: batches[0] })
          return {
            type: 'MOVE',
            data: {
              [lotKey]: batches[0],
            } as any,
            nextState: shouldScanSerialStateName,
          };
        }
      }
    },
    onAction: async (args: OnActionArgs<S, P, R>) => {
      const input = args.inputs[0];
      const item = args.state[itemKey] as unknown as IItem;
      const { batchCode } = await compoundItemBarcodeParser(input, () => [args.state[itemKey] as unknown as IItem], 'batchCode')
      const batch = await findAllowedLots(args).find(b => b.toLowerCase().trim() === batchCode.toLowerCase().trim())
      if (isNil(batch)) {
        throw addNotification('danger', <Translation name="T.errors.itemIdentification.unknownBatchForCode" params={{ code: batchCode }} />);
      }
      await window.be_specializations?.augmentations?.identification?.lotSelected?.({ item, lot: batch })
      return {
        type: 'MOVE',
        data: {
          [lotKey]: batch,
        } as any,
        nextState: shouldScanSerialStateName,
      };
    },

    attributes: (args: OnEnterArgs<S, P, R>) => {
      const { state } = args
      let autocompleter: AutoCompleteFetcher = null
      if (binLocationContentsKey) {
        const contents = state[binLocationContentsKey] as unknown as IItemBinLocation[];
        const item = state[itemKey] as unknown as IItem;
        const lots = distinctBy(mapNotNil(contents.filter(it => it.item.code === item.code), it => it.serialNumber?.lotCode), it => it)
        autocompleter = async s => simpleAutocompleteOptions(lots, s) as any[]
      } else {
        autocompleter = async s => simpleAutocompleteOptions(await findAllowedLots(args), s) as any[]
      }

      return {
        hint: 'T.hints.lot',
        autocompleter,
        ...(serialStateProps.lotAttributes || {})
      }
    },

    nodeName: 'T.states.lot',
    outputs: [
      {
        name: 'onLot',
        to: shouldScanSerialStateName,
      },
    ],
  }

  const serialState: State<S, EventProps, Attributes, R> = {
    onEnter: async (args: OnEnterArgs<S, P, R>) => {
      // check the compound
      const compound = args.state[compoundBarcodeKey] as CompoundBarcode
      const item = args.state[itemKey] as unknown as IItem;
      if (!isNil(compound?.serialCode)) {
        const foundSerial = (await findAllowedSerials(args)).find(b => matchSerial(b, compound.serialCode))
        await window.be_specializations?.augmentations?.identification?.serialSelected?.({ item, serial: foundSerial })
        if (foundSerial) {
          return {
            type: 'MOVE',
            data: {
              [serialKey]: foundSerial,
              [quantityKey]: 1.0,
            } as any,
            nextState,
          };
        } else {
          addNotification('danger', <Translation name="T.errors.itemIdentification.unknownSerialForCode" params={{ code: compound?.serialCode }} />);
          args.rollbackUntilAndInterruptFirst(itemStateName)
          return
        }
      }

      // attempt auto fill
      if (args.props.config.settings.scannerItemIdentificationSerialAllowAutoSelectIfOnlyOneOnLocation && binLocationContentsKey) {
        const contents = args.state[binLocationContentsKey] as unknown as IItemBinLocation[];
        const item = args.state[itemKey] as unknown as IItem;
    
        const serials = distinctBy(
          contents.filter((it) => it.item.code === item.code).map(it => it.serialNumber),
          (it) => it.code
        );
        if (serials.length === 1) {
          const foundSerial = (await findAllowedSerials(args)).find(b => matchSerial(b, serials[0].code))
          if (foundSerial) {
            await window.be_specializations?.augmentations?.identification?.serialSelected?.({ item, serial: foundSerial })
            return {
              type: 'MOVE',
              data: {
                [serialKey]: foundSerial,
                [quantityKey]: 1.0,
              } as any,
              nextState,
            };
          } else {
            addNotification('danger', <Translation name="T.errors.itemIdentification.unknownSerialForCode" params={{ code: compound?.serialCode }} />);
            args.rollbackUntilAndInterruptFirst(itemStateName)
            return
          }
        }
      }

      if (args.props.config.settings.scannerItemIdentificationSerialAllowAutoSelectIfOnlyOneAllowed) {      
        const serials = await findAllowedSerials(args)
        if (serials.length === 1) {
          await window.be_specializations?.augmentations?.identification?.serialSelected?.({ item, serial: serials[0] })
          return {
            type: 'MOVE',
            data: {
              [serialKey]: serials[0],
              [quantityKey]: 1.0,
            } as any,
            nextState,
          };
        }
      }
    },
    onAction: async (args: OnActionArgs<S, P, R>) => {
      const input = args.inputs[0];
      const item = args.state[itemKey] as unknown as IItem;
      const { serialCode } = await compoundItemBarcodeParser(input, () => [args.state[itemKey] as unknown as IItem], 'serialCode')
      const serial = await findAllowedSerials(args).find(b => matchSerial(b, serialCode))
      if (!serial) {
        throw addNotification('danger', <Translation name="T.errors.itemIdentification.unknownSerialForCode" params={{ code: serialCode }} />);
      }
      await window.be_specializations?.augmentations?.identification?.serialSelected?.({ item, serial: serial })
      return {
        type: 'MOVE',
        data: {
          [serialKey]: serial,
          [quantityKey]: 1.0,
        } as any,
        nextState,
      };
    },

    attributes: (args: OnEnterArgs<S, P, R>) => {
      const { state } = args
      let autocompleter: AutoCompleteFetcher = null
      if (binLocationContentsKey) {
        const contents = state[binLocationContentsKey] as unknown as IItemBinLocation[];
        const item = state[itemKey] as unknown as IItem;
        const serials = distinctBy(mapNotNil(contents.filter(it => it.item.code === item.code), it => it.serialNumber), it => it.code)
        autocompleter = async s => serialAutocompleteOptions(serials, s) as any[]
      } else {
        autocompleter = async s => serialAutocompleteOptions(await findAllowedSerials(args), s) as any[]
      }

      return {
        hint: 'T.hints.serial',
        autocompleter,
        ...(serialStateProps.serialAttributes || {})
      }
    },


    nodeName: 'T.states.serial',
    outputs: [
      {
        name: 'serial',
        to: nextState,
      },
    ],
  }


  const onItemSetContinueState: State<S, EventProps, Attributes, R> = {
    onEnter(args) {
      return {
        type: 'MOVE',
        nextState: ((args.state as any)['__be_item_identification_next_state']),
      };  
    },
    nodeName: 'T.states.itemIdentificationAfterItemContinue',
    outputs: [
      {
        name: 'onLiquid',
        to: nextState,
      },
      {
        name: 'onNormal',
        to: nextState,
      },
      {
        name: 'onBatch',
        to: batchStateName,
      },
      {
        name: 'onSerial',
        to: lotStateName,
      },
    ], 
  }

  return {
    [itemStateName]: itemState,

    [batchStateName]: batchState,

    [lotStateName]: lotState,

    [shouldScanSerialStateName]: StateDecide<S, EventProps, Attributes, R>({
      nextStateIfFalse: nextState,
      nextStateIfTrue: serialStateName,
      decide: async (args) => {
        const item = args.state[itemKey] as unknown as IItem
        return !item.serialManagedByDispatch
      }
    }),

    [serialStateName]: serialState,

    ...(onItemSetContinueStateName ? { [onItemSetContinueStateName]: onItemSetContinueState } : {})
  } as any
}

export default createItemIdentifications